import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { GiftIcon, PlayIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { ProductImage } from '../../../ui/ProductImage';
import { useCatalog } from '../../../contexts/CatalogContext';
import { useSignedInAccount } from '../../../contexts/AccountContext';
import { euro } from '../../../core/format/currency';
import { ordinal, policyCopy } from '../../../data/policies';
import { useSite } from '../../../contexts/SiteContext';
import { dayLong, dayShort, nextIsGift, relative } from '../../../utils/account';
import { canChangeDelivery, changeDeadline } from '../../../core/rules/subscription';
import { postponeLimit } from '../../../core/rules/refillPlan';
import { subscriptionRefill } from '../../../utils/refill';
import { btnPrimary, btnSecondary } from '../../../ui/styles';
import { isSoldOut } from '../../../core/rules/stock';
import { shopLabels } from '../../../data/shopLabels';
import { ease } from '../../../core/format/motion';
import { DeliveryQuickActions } from './DeliveryQuickActions';
import { RefillUsage } from './RefillUsage';
const iconBtn = 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted transition-colors duration-150 ease-soft hover:bg-sand hover:text-ink';
const quiet = 'min-h-[40px] text-[13px] text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline disabled:pointer-events-none disabled:opacity-40';

/**
 * De volgende doos, op Thuis › Levering. Slim bijvullen: alleen wat vóór de doos erna op raakt
 * (core/rules/refillPlan.ts). Per product "deze keer niet"; zakt de doos onder het minimum, dan
 * kiest ze: later (samen met het volgende product), iets toevoegen, of toch meesturen.
 */
export function NextDeliveryCard() {
  const {
    policy
  } = useSite();
  const {
    account,
    subscription,
    changeSubscription
  } = useSignedInAccount();
  const {
    productById
  } = useCatalog();
  const reduce = useReducedMotion();
  const sub = account.subscription;
  if (!sub) return null;
  if (sub.status !== 'actief') {
    const paused = sub.status === 'gepauzeerd';
    return <section aria-labelledby="delivery-title" className="bg-shell p-5 ring-1 ring-line sm:p-7" data-id="element-6143" data-mp="207rzbv7kxm35">
        <p className="text-[14px] text-muted" data-id="element-6144" data-mp="254rwfgmgbei4">{paused ? 'Gepauzeerd' : 'Gestopt'}</p>
        <h2 id="delivery-title" className="mt-1 font-display text-[24px] leading-tight text-ink sm:text-[28px]" data-id="element-6145" data-mp="298purp3ycla4">
          {paused && sub.pausedUntil ? `Tot en met ${dayLong(sub.pausedUntil)}` : 'Je abonnement staat stil'}
        </h2>
        <p className="mt-2 max-w-md text-[14px] leading-relaxed text-muted" data-id="element-6146" data-mp="2j419w8j9vuie">
          {paused ? 'Daarna gaat het vanzelf verder. We sturen je een week vooraf een seintje.' : 'Je producten en ritme hebben we bewaard. Opnieuw beginnen is één tik.'}
        </p>
        <button type="button" onClick={async () => {
        if (await subscription({
          type: 'resume'
        })) toast.success('Je abonnement loopt weer', {
          description: `${policyCopy.firstDelivery}.`
        });
      }} className={`${btnPrimary} mt-5`} data-id="element-6147" data-mp="2wyk9kc7u0jx0">
          <PlayIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6148" data-mp="l9w2ztnk94gb" />
          {paused ? 'Nu al hervatten' : 'Opnieuw starten'}
        </button>
      </section>;
  }
  const {
    items,
    boxes
  } = subscriptionRefill(sub, productById, policy.subscription);
  const [box, ...later] = boxes;
  const skipped = (sub.skipNext ?? []).filter(id => sub.items.some(i => i.productId === id));
  const gift = nextIsGift(sub);
  const deadline = changeDeadline(sub.nextDelivery, policy.subscription);
  const locked = !canChangeDelivery(sub.nextDelivery, policy.subscription);
  const min = policy.subscription.minBoxValue;
  const tooSmall = box.lines.length > 0 && box.total < min;
  const limit = postponeLimit(items, box, policy.subscription);
  // Wat niet in deze doos zit, en wanneer het wel komt.
  const comesLater = sub.items.filter(i => !box.lines.some(l => l.productId === i.productId) && !skipped.includes(i.productId)).map(i => ({
    id: i.productId,
    product: productById(i.productId),
    at: later.find(b => b.lines.some(l => l.productId === i.productId))?.date
  }));
  const runsOutOf = (id: string) => items.find(i => i.productId === id)?.runsOutOn;
  const toggleSkip = (productId: string) => subscription({
    type: 'toggleSkipItem',
    productId
  });
  const sendAnyway = async () => {
    for (const id of skipped) await subscription({
      type: 'toggleSkipItem',
      productId: id
    });
  };
  const removeItem = async (productId: string) => {
    if (sub.items.length <= 1) {
      toast('Dit is je laatste product', {
        description: 'Wil je even niets? Pauzeer dan, of stop onderaan de pagina.'
      });
      return;
    }
    const undo = await changeSubscription({
      type: 'removeItem',
      productId
    });
    if (!undo) return;
    toast('Uit je abonnement gehaald', {
      description: productById(productId)?.name,
      action: {
        label: 'Ongedaan maken',
        onClick: () => void undo()
      }
    });
  };
  return <section aria-labelledby="delivery-title" className="bg-shell ring-1 ring-line" data-id="element-6149" data-mp="2o9d5c77nyba8">
      <div className="p-5 sm:p-7" data-id="element-6150" data-mp="2k8jilh5yj2ix">
        <h2 id="delivery-title" className="text-[14px] text-muted" data-id="element-6151" data-mp="2k7i7xdsusi1q">
          Volgende doos · <span className="inline-block first-letter:uppercase" data-id="element-6152" data-mp="2xzy4pwv5vju7">{relative(sub.nextDelivery)}</span>
        </h2>
        <AnimatePresence mode="wait" initial={false} data-id="element-6153" data-mp="2t1mx7ssc9lf5">
          <motion.p key={sub.nextDelivery} aria-live="polite" initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.2,
          ease
        }} className="mt-1 font-display text-[30px] leading-tight text-ink sm:text-[40px]" data-id="element-6154" data-mp="25b0ywt84zfic">
            <span className="capitalize" data-id="element-6155" data-mp="3qnu81kf8k9zy">{dayLong(sub.nextDelivery)}</span>
          </motion.p>
        </AnimatePresence>
        <p className="mt-1 text-[14px] text-muted" data-id="element-6156" data-mp="3lo7mmnp97ng2">
          {locked ? 'Deze doos ligt vast: Sarah pakt hem nu in. Wijzigingen gelden voor de doos daarna.' : `Alleen wat bijna op is. Aanpassen kan tot en met ${dayShort(deadline)}.`}
        </p>

        {gift && <p className="mt-4 flex items-center gap-2 bg-blush/50 px-3 py-2.5 text-[14px] text-ink" data-id="element-6157" data-mp="6lil8iyt2ukc">
            <GiftIcon className="h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6158" data-mp="bk3l2ha5bc89" />
            Dit is je {ordinal(sub.deliveriesDone + 1)} doos: er zit {policy.subscription.giftDescription} bij.
          </p>}

        <ul className="mt-6 divide-y divide-line border-y border-line" aria-label="In deze doos" data-id="element-6159" data-mp="3f1u5sgm1jojv">
          {box.lines.map(line => {
          const product = productById(line.productId);
          if (!product) return null;
          const extra = sub.extras.includes(line.productId) && !sub.items.some(i => i.productId === line.productId);
          const out = runsOutOf(line.productId);
          return <li key={line.productId} className="flex items-center gap-3 py-4 sm:gap-4" data-id="element-6160" data-mp="2w8q6ofq20jhv">
                <span className="h-14 w-14 shrink-0 bg-sand" data-id="element-6161" data-mp="2oqaao6b0i8jx">
                  <ProductImage src={product.image} alt="" className="h-full w-full p-1" data-id="element-6162" data-mp="2jrpa25rulmuy" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-6163" data-mp="2eruehox4o93k">
                  <span className="block truncate text-[15px] text-ink" data-id="element-6164" data-mp="29tt03hancxyf">
                    {line.qty > 1 && <span className="tabular-nums" data-id="element-6165" data-mp="29w5dri9dgm5o">{line.qty}× </span>}
                    {product.name}
                  </span>
                  <span className="block text-[13px] text-muted" data-id="element-6166" data-mp="210xps96boc1s">
                    {euro(line.price * line.qty)}
                    {extra ? <span className="text-clay" data-id="element-6167" data-mp="1w360zo0z561t"> · eenmalig</span> : out && ` · op ± ${dayShort(out)}`}
                    {isSoldOut(product) && <span className="text-clay" data-id="element-6168" data-mp="1r54f6vc5ysmn"> · {shopLabels.soldOutShort}</span>}
                  </span>
                </span>
                {extra ? <button type="button" onClick={() => void subscription({
              type: 'toggleExtra',
              productId: line.productId
            })} disabled={locked} aria-label={`${product.name} weer weghalen uit deze doos`} className={`${iconBtn} disabled:pointer-events-none disabled:opacity-40`} data-id="element-6169" data-mp="1i2d6h3aoaayp">
                    <XIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6170" data-mp="30zf2izc1ymgh" />
                  </button> : <span className="flex shrink-0 items-center gap-1" data-id="element-6171" data-mp="2n7idj45oksob">
                    <button type="button" onClick={() => void toggleSkip(line.productId)} disabled={locked} className={quiet} data-id="element-6172" data-mp="2s0l91hu0qc75">
                      Deze keer niet
                    </button>
                    <button type="button" onClick={() => void removeItem(line.productId)} disabled={locked} aria-label={`${product.name} uit je abonnement halen`} className={`${iconBtn} disabled:pointer-events-none disabled:opacity-40`} data-id="element-6173" data-mp="2i8x9sur7zqv4">
                      <XIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6174" data-mp="1ko5nmkrw7su3" />
                    </button>
                  </span>}
              </li>;
        })}
          {skipped.map(id => <li key={`skip-${id}`} className="flex items-center justify-between gap-3 py-3 text-[14px]" data-id="element-6175" data-mp="1tnsliwleygie">
              <span className="min-w-0 truncate text-muted" data-id="element-6176" data-mp="28kth5b6q6vm5">
                <span className="line-through" data-id="element-6177" data-mp="23n9pn9l0omc9">{productById(id)?.name ?? id}</span> · deze keer niet
              </span>
              <button type="button" onClick={() => void toggleSkip(id)} disabled={locked} className={quiet} data-id="element-6178" data-mp="2heocjri1p7ol">
                Toch meesturen
              </button>
            </li>)}
          {!box.lines.length && <li className="py-4 text-[14px] text-muted" data-id="element-6179" data-mp="2rd41poxii6vg">Niets is bijna op. Deze doos gaat niet; de volgende staat hieronder.</li>}
        </ul>

        {tooSmall && !locked && <div role="status" className="mt-4 bg-sand px-4 py-4" data-id="element-6180" data-mp="27jobj9cm46vh">
            <p className="text-[14px] text-ink" data-id="element-6181" data-mp="22rmpwwtk13ss">
              Deze doos is {euro(box.total)}. Een doos is altijd minstens {euro(min)}, dan blijft verzending gratis.
            </p>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-6182" data-mp="1trghhpcpqu0h">
              {limit > box.date && <button type="button" onClick={async () => {
            if (await subscription({
              type: 'postponeTo',
              date: limit
            })) toast.success(`Doos verschoven naar ${dayShort(limit)}`);
          }} className={btnSecondary} data-id="element-6183" data-mp="2qhqt532tz1j9">
                  Doos naar {dayShort(limit)}
                </button>}
              <a href="#order-bump" className={btnSecondary} data-id="element-6184" data-mp="161vxr48qjsq5">
                Iets toevoegen
              </a>
              {skipped.length > 0 && <button type="button" onClick={() => void sendAnyway()} className={btnSecondary} data-id="element-6185" data-mp="1121791svh55m">
                  Toch alles meesturen
                </button>}
            </div>
          </div>}

        <p className="mt-4 flex items-baseline justify-between gap-4 text-[15px]" data-id="element-6186" data-mp="vy8fal8brort">
          <span className="text-muted" data-id="element-6187" data-mp="10vh0kuors8xa">Totaal, altijd zonder verzendkosten</span>
          <span className="tabular-nums text-ink" data-id="element-6188" data-mp="15otm2vy5jzlt">{euro(box.total, {
            cents: true
          })}</span>
        </p>

        {comesLater.length > 0 && <p className="mt-3 text-[13px] leading-relaxed text-muted" data-id="element-6189" data-mp="19rg6wcweok9i">
            Komt later:{' '}
            {comesLater.map((c, i) => <span key={c.id} data-id="element-6190" data-mp="1jmzywo7umysz" data-mpi={i}>
                {i > 0 && ', '}
                {c.product?.name ?? c.id}
                {c.at && ` (${dayShort(c.at)})`}
              </span>)}
          </p>}

        <div className="mt-6" data-id="element-6191" data-mp="3lfy13anin3ql">
          <DeliveryQuickActions data-id="element-6192" data-mp="3lgqxbhoumauh" />
        </div>
        <RefillUsage data-id="element-6193" data-mp="3qdhek8ce3chh" />
      </div>
    </section>;
}